import React from "react";
import { motion } from "framer-motion";
import {
  Plane,
  Briefcase,
  MapPin,
  Clock,
  Shield,
  Wifi,
  Users,
  Star,
  CheckCircle,
  ArrowRight,
  Car,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useBookingModal } from "../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const ServicesPage: React.FC = () => {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();
  const mainServices = [
    {
      icon: Plane,
      title: t("1428", "Airport Services"),
      description: t(
        "1429",
        "Do you need a taxi from an airport in Western Europe? Then you've come to the right site. We provide transportation from any airport in Western Europe. With us, you can count on excellent service at an affordable price."
      ),
      features: [
        t("1430", "Transportation from any Western European airport"),
        t("1431", "Excellent service at affordable prices"),
        t("1432", "Quick and safe transportation"),
        t("1433", "Available 7 days a week, 24 hours a day"),
      ],
      gradient: "from-primary to-primary-dark",
      image: "/images/ci1.webp",
    },
    {
      icon: Briefcase,
      title: t("1434", "Business Transfers"),
      description: t(
        "1435",
        "At Taxi Schiphol Airport, we specialize in professional, seamless transportation solutions for business travelers. Our services are designed to cater to the unique demands of corporate clients."
      ),
      features: [
        t("1436", "Professional business transportation"),
        t("1437", "Airport representative service"),
        t("1438", "Flexible day rentals"),
        t("1439", "Modern fleet for all group sizes"),
      ],
      gradient: "from-accent to-primary-light",
      image: "/images/c2.webp",
    },
    {
      icon: MapPin,
      title: t("1440", "City Transfers"),
      description: t(
        "1441",
        "Need reliable transport around the city? With our city transport service, you can enjoy smooth, comfortable rides to any destination, from attractions to business appointments."
      ),
      features: [
        t("1442", "Reliable city transportation"),
        t("1443", "Hourly booking options"),
        t("1444", "Full day reservations"),
        t("1445", "Explore at your own pace"),
      ],
      gradient: "from-warning to-primary",
      image: "/images/c3.webp",
    },
  ];

  const businessFeatures = [
    {
      icon: Users,
      title: t("1446", "Airport Representative"),
      description: t(
        "1447",
        "Our experienced drivers serve as your representative at Schiphol, welcoming clients with professional presence."
      ),
    },
    {
      icon: Clock,
      title: t("1448", "Flexible Day Rentals"),
      description: t(
        "1449",
        "Rent our services for the entire day, moving seamlessly from meetings to conferences without waiting."
      ),
    },
    {
      icon: Car,
      title: t("1450", "Modern Fleet"),
      description: t(
        "1451",
        "Accommodate any group size with our well-maintained vehicles, including VIP and executive options."
      ),
    },
    {
      icon: Wifi,
      title: t("1452", "Premium Services"),
      description: t(
        "1453",
        "On-Board Wi-Fi, comfortable seating, confidentiality and customizable routes for your convenience."
      ),
    },
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/background/bg1.webp"
            alt={t("1454", "Professional Services")}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/90 to-gray-800/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
            >
              <Car className="h-4 w-4" />
              <span>{t("1455", "Our Premium Services")}</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              {t("1456", "Professional")}{" "}
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {t("1457", "Transportation")}
              </span>{" "}
              {t("1458", "Services")}
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              {t(
                "1459",
                "Everything you need from your taxi business is here! Professional service for every journey."
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1460", "Our Core Services")}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t(
                "1461",
                "Comprehensive transportation solutions tailored to your needs"
              )}
            </p>
          </motion.div>

          <div className="space-y-24">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                  index % 2 === 1 ? "lg:grid-flow-col-dense" : ""
                }`}
              >
                {/* Content */}
                <div
                  className={`space-y-8 ${
                    index % 2 === 1 ? "lg:col-start-2" : ""
                  }`}
                >
                  <div>
                    <div
                      className={`inline-flex items-center justify-center w-16 h-16 bg-linear-to-r ${service.gradient} rounded-2xl mb-6`}
                    >
                      <service.icon className="h-8 w-8 text-white" />
                    </div>

                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                      {service.title}
                    </h3>

                    <p className="text-lg text-gray-200 leading-relaxed mb-8">
                      {service.description}
                    </p>
                  </div>

                  {/* Features List */}
                  <div className="space-y-4">
                    {service.features.map((feature) => (
                      <motion.div
                        key={feature}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.5,
                        }}
                        className="flex items-center space-x-3"
                      >
                        <CheckCircle className="h-5 w-5 text-primary shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </motion.div>
                    ))}
                  </div>

                  <motion.div
                    whileHover={{ x: 5 }}
                    className="inline-flex items-center text-primary font-semibold hover:text-primary-light transition-colors duration-300"
                  >
                    <span className="mr-2">{t("1462", "Learn More")}</span>
                    <ArrowRight className="h-4 w-4" />
                  </motion.div>
                </div>

                {/* Image */}
                <div
                  className={`relative ${
                    index % 2 === 1 ? "lg:col-start-1" : ""
                  }`}
                >
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className="relative overflow-hidden rounded-3xl shadow-2xl"
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[400px] object-cover"
                    />
                    <div
                      className={`absolute inset-0 bg-linear-to-t ${service.gradient} opacity-20`}
                    />

                    {/* Floating Badge */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6 }}
                      className="absolute top-6 right-6 bg-gray-800/95 backdrop-blur-xs p-4 rounded-2xl"
                    >
                      <div className="flex items-center space-x-2">
                        <Star className="h-5 w-5 text-warning fill-current" />
                        <span className="text-white font-semibold">
                          {t("1463", "Premium Service")}
                        </span>
                      </div>
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Transfer Details */}
      <section className="py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1464", "Business Transfer Excellence")}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t(
                "1465",
                "Tailored solutions for corporate clients with the highest level of reliability and efficiency"
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessFeatures.map((feature) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                whileHover={{ y: -5 }}
                className="bg-gray-800/50 backdrop-blur-xs rounded-2xl p-6 border border-gray-700 hover:border-primary/50 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-linear-to-r from-primary to-primary-light rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Guarantee */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-12 border border-gray-700">
              <Shield className="h-16 w-16 text-primary mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {t("1466", "Our Service Guarantee")}
              </h2>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                {t(
                  "1467",
                  "Book a taxi with us and you can be sure that our taxi will be ready for you at the agreed time and location. We are precise and reliable in our profession. Our well-trained drivers will welcome you hospitably and make your ride as pleasant as possible."
                )}
              </p>
              <div className="flex flex-wrap justify-center gap-6 mb-8">
                {[
                  t("1468", "Precise & Reliable"),
                  t("1469", "Well-trained Drivers"),
                  t("1470", "Hospitable Service"),
                  t("1471", "Pleasant Experience"),
                ].map((guarantee) => (
                  <div
                    key={guarantee}
                    className="flex items-center space-x-2 bg-primary/20 text-primary px-4 py-2 rounded-full"
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">{guarantee}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-linear-to-r from-primary via-primary-light to-primary rounded-3xl p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("1472", "Ready to Book Your Premium Service?")}
              </h2>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                {t(
                  "1473",
                  "Experience stress-free business travel with TaxiSchipholAirport. Let us handle the transportation while you focus on what matters most."
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openModal}
                  className="bg-white text-primary px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg"
                >
                  {t("1474", "Book Your Business Transfer")}
                </motion.button>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300"
                  >
                    {t("1475", "Contact Us")}
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
